import { SignUpGuardProvider } from '@app/hooks/SignUpGuardContext';
import {
  Box,
  constants,
  PageSection,
  Stack,
  Text,
} from '@redbubble/design-system';
import { ErrorBoundary } from '@redbubble/next/error-boundary';
import { FormattedMessage } from '@redbubble/next/intl';
import dynamic from 'next/dynamic';
import { NextPage } from 'next/types';
import { useMemo } from 'react';
import { TrustSignalsGroup } from '../../../i/components/TrustSignalsGroup';
import type { CategorySearchResultsPageProps } from '../../types';
import { L2Carousel } from '../L2Carousel';

const SignUpGuard = dynamic(
  () =>
    import(
      /* webpackChunkName: "SignUpGuard" */
      '../../../../components/SignUpGuard/SignUpGuard'
    ).then(({ SignUpGuard: Component }) => Component),
  { ssr: false, loading: () => null }
);

export const CategoryPage: NextPage<CategorySearchResultsPageProps> = ({
  config,
  carouselData,
  iaCode,
}) => {
  const resultsBySlug = useMemo(
    () => Object.fromEntries(carouselData.map((d) => [d.slug, d.results])),
    [carouselData]
  );

  return (
    <SignUpGuardProvider>
      <SignUpGuard />
      <Box element="main">
        <PageSection size={constants.MEDIUM}>
          <Stack spacing="l">
            <Text element="h1" type="display1">
              <FormattedMessage {...config.title} />
            </Text>
            {/*
             * Placeholder for the L2 pills
             * https://articore.atlassian.net/browse/DEAP-4186
             */}
            <Text
              element="p"
              type="body"
              display="block"
              muted
              data-testid="category-pills-placeholder"
            >
              L2 pills coming soon
            </Text>
            <Stack spacing="xl">
              {config.l2Categories
                .filter((l2) => l2.active)
                .map((l2) => (
                  <L2Carousel
                    key={l2.slug}
                    title={l2.title}
                    results={resultsBySlug[l2.slug] ?? []}
                    seeAllPath={
                      iaCode
                        ? `${l2.seeAllPath}&iaCode=${iaCode}`
                        : l2.seeAllPath
                    }
                    slug={l2.slug}
                  />
                ))}
            </Stack>
            {/*
             * TODO(DEAP): Confirm whether SPC / related-topics content. Placeholder only for now
             * remove if product decides it's out of scope.
             */}
            <Text
              element="p"
              type="body"
              display="block"
              muted
              data-testid="category-related-content-placeholder"
            >
              SPC / related topics content coming soon (confirm scope)
            </Text>
            <ErrorBoundary>
              <TrustSignalsGroup />
            </ErrorBoundary>
          </Stack>
        </PageSection>
      </Box>
    </SignUpGuardProvider>
  );
};
